// Filtros de la hoja de Obras como dato: la regla ÚNICA con la que la hoja decide qué
// obras se ven y con la que el API decide qué obras lleva un calendario suscrito.
//
// El calendario de la Agenda guarda el filtro (no la lista de obras) y el API lo vuelve a
// aplicar en cada refresco de Outlook o Apple Calendar: una obra nueva que cumple el
// filtro entra sola. El API tiene una copia en C# (BookBSK/Services/ObrasFiltro.cs); las
// dos se comprueban con el mismo caso de referencia (__fixtures__/filtro-obras.json).
// Si cambia una regla de aquí, hay que cambiarla allí y regenerar ese caso.
import { obraHitoAlerts } from "./hitosModel";
import { daysBetween, getCapexDeviation } from "./portfolio/portfolioShared";

export const OBRA_FILTER_VERSION = 1;

// Texto comparable: sin tildes y en minúsculas (búsqueda de la hoja).
export function normalizeSearchValue(value) {
    return String(value ?? "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
}

// ── Obra tal como la usa la hoja a partir de la fila del API ─────────────────────
// Fechas DATE que el API manda con hora: se quedan en AAAA-MM-DD, y la fecha vacía
// histórica de SQL Server (1900-01-01) queda en "".
export const API_DATE_FIELDS = ["inicioObra", "apertura", "fechaCierre", "ventanaInicio", "ventanaFin", "zonif", "legal", "planoP3", "deadlineMob", "fechaIluminacion", "fechaSuelo", "p3Constructora", "p3Mobiliario", "inicioMontajeMob", "finMontajeMob", "zonifReal", "legalReal", "planoP3Real", "deadlineMobReal", "p3ConstructoraReal", "p3MobiliarioReal"];

// Secciones de la ficha (proveedor + fecha + estado) que también entran en la búsqueda.
export const milestoneStatusLabels = { PENDIENTE: "Pendiente", "EN CURSO": "En curso", OK: "Completado", RIESGO: "Riesgo", NA: "No aplica" };
export const obraSectionFields = [
    ["Imagen", "imagen", "zonif"], ["Legal", null, "legal"], ["P3", null, "planoP3"],
    ["Mobiliario", "mobiliario", "deadlineMob"], ["Iluminacion", "provIlum1", "fechaIluminacion"],
    ["Suelo", "sueloGral", "fechaSuelo"],
];

const dateAtNoon = (value) => (value ? new Date(`${value}T12:00:00`) : null);

// Estado de una sección sin estado registrado, por su fecha. `today`: Date a mediodía local.
export function sectionStatus(deadline, openingDate, today) {
    const date = dateAtNoon(deadline);
    const opening = dateAtNoon(openingDate);
    if (!date) return opening && opening < today ? "Completado" : "Pendiente";
    if (date < today) return "Completado";
    return (date - today) / 86400000 <= 21 ? "En curso" : "Pendiente";
}

export function buildObraSections(obra, today) {
    return obraSectionFields.map(([seccion, proveedorField, fechaField]) => ({
        seccion,
        proveedor: (proveedorField && obra[proveedorField]) || "",
        deadline: obra[fechaField] || "",
        estado: milestoneStatusLabels[obra[`${fechaField}Estado`]] || sectionStatus(obra[fechaField] || "", obra.apertura, today),
        estadoRegistrado: obra[`${fechaField}Estado`] ?? null,
    }));
}

// Fila del API -> obra de la hoja (sin los agregados de la interfaz, que no filtran).
export function apiObra(row, today) {
    const obra = { ...row };
    for (const field of API_DATE_FIELDS) {
        if (typeof obra[field] === "string") {
            obra[field] = obra[field].slice(0, 10);
            if (obra[field] === "1900-01-01") obra[field] = "";
        }
    }
    return {
        ...obra,
        // Duración de obra calculada (inicio -> apertura); no es un dato que se teclee.
        dias: (obra.inicioObra && obra.apertura) ? daysBetween(obra.inicioObra, obra.apertura) : obra.dias,
        secciones: buildObraSections(obra, today),
    };
}

// Texto de búsqueda de una obra: cualquier campo de texto (los importes y medidas son
// números y quedan fuera) y el nombre, estado y proveedor de cada sección.
export function obraSearchText(obra) {
    const parts = [];
    Object.values(obra).forEach((value) => {
        if (typeof value === "string" && value.trim()) parts.push(value);
    });
    (obra.secciones || []).forEach((section) => {
        parts.push(section.seccion, section.estado, section.proveedor);
    });
    return normalizeSearchValue(parts.join(" "));
}

// ── Valores por los que se filtra ────────────────────────────────────────────────
// Campos calculados; el resto de filtros usan el campo de la obra tal cual.
export const HITO_SITUACION_SIN_ALERTAS = "Sin alertas";
const DERIVED_FILTER_VALUES = {
    // Situación más grave de sus hitos críticos.
    hitoSituacion: (obra, today) => {
        const [worst] = obraHitoAlerts(obra, today);
        return worst ? worst.statusLabel : HITO_SITUACION_SIN_ALERTAS;
    },
    margen: (obra) => getCapexDeviation(obra),
    eurom2: (obra) => Number(obra.euroM2Inv || 0),
    bloq: (obra, today) => obraHitoAlerts(obra, today).length,
};
export const DERIVED_FILTER_IDS = Object.keys(DERIVED_FILTER_VALUES);

// Valor de un filtro de categoría que elige las obras SIN dato en ese campo. En la hoja
// es una opción más de la lista («No informado»), guardada como "". El API manda los campos sin
// dato como null, y la regla compara tal cual (includes): por eso, al armar el filtro,
// a "" se le añade null (ver obraFilterSpec). Así la regla no cambia, ni aquí ni en su
// copia del API, y un calendario suscrito con este filtro funciona igual.
export const EMPTY_FILTER_VALUE = "";
export const EMPTY_FILTER_LABEL = "No informado";
export const filterValueLabel = (value) => (value == null || value === EMPTY_FILTER_VALUE ? EMPTY_FILTER_LABEL : String(value));

export function filterFieldValue(obra, fieldId, today) {
    const derived = DERIVED_FILTER_VALUES[fieldId];
    return derived ? derived(obra, today) : obra[fieldId];
}

// ── El filtro como dato ──────────────────────────────────────────────────────────
// { v, busqueda, estado, pais, pm, categoria: {campo: [valores]}, numero: {campo: {min, max}},
//   fecha: {campo: {min, max}} }. Los selectores rápidos van solo si no están en «Todos».
// `fieldIds`: { category, number, date } con los campos de la hoja de cada tipo (un campo
// que no es de ese tipo no filtra, igual que en la hoja).
export function obraFilterSpec({ search = "", estado = "Todos", pais = "Todos", pm = "Todos", customFilters = {}, fieldIds }) {
    const spec = { v: OBRA_FILTER_VERSION };
    const busqueda = String(search ?? "").trim();
    if (busqueda) spec.busqueda = busqueda;
    if (estado !== "Todos") spec.estado = estado;
    if (pais !== "Todos") spec.pais = pais;
    if (pm !== "Todos") spec.pm = pm;
    const categoria = {};
    Object.entries(customFilters.category || {}).forEach(([id, values]) => {
        if (!fieldIds.category.has(id) || !Array.isArray(values) || !values.length) return;
        // «No informado» elige las obras sin dato: "" y null (lo que manda el API).
        categoria[id] = values.includes(EMPTY_FILTER_VALUE) ? [...values.filter((value) => value !== null), null] : [...values];
    });
    const ranges = (source, ids) => {
        const out = {};
        Object.entries(source || {}).forEach(([id, range]) => {
            if (!ids.has(id) || !range) return;
            const min = range.min ?? "";
            const max = range.max ?? "";
            if (min !== "" || max !== "") out[id] = { min, max };
        });
        return out;
    };
    const numero = ranges(customFilters.number, fieldIds.number);
    const fecha = ranges(customFilters.date, fieldIds.date);
    if (Object.keys(categoria).length) spec.categoria = categoria;
    if (Object.keys(numero).length) spec.numero = numero;
    if (Object.keys(fecha).length) spec.fecha = fecha;
    return spec;
}

export function isEmptyObraFilter(spec) {
    return !spec?.busqueda && !("estado" in (spec || {})) && !("pais" in (spec || {})) && !("pm" in (spec || {}))
        && !Object.keys(spec?.categoria || {}).length && !Object.keys(spec?.numero || {}).length && !Object.keys(spec?.fecha || {}).length;
}

// ¿La obra cumple el filtro? `searchText`: texto de búsqueda ya calculado (la hoja lo
// cachea); si no se pasa, se calcula. No mira si la obra está archivada: eso lo decide
// quien filtra (la hoja, con su conmutador; el calendario, siempre solo activas).
export function obraMatchesFilter(obra, spec, today, searchText) {
    if (spec.busqueda) {
        const needle = normalizeSearchValue(String(spec.busqueda).trim());
        if (needle && !(searchText ?? obraSearchText(obra)).includes(needle)) return false;
    }
    if ("estado" in spec && obra.estado !== spec.estado) return false;
    if ("pais" in spec && obra.pais !== spec.pais) return false;
    if ("pm" in spec && obra.pm !== spec.pm) return false;
    for (const [id, selected] of Object.entries(spec.categoria || {})) {
        if (selected.length && !selected.includes(filterFieldValue(obra, id, today))) return false;
    }
    for (const [id, range] of Object.entries(spec.numero || {})) {
        const value = filterFieldValue(obra, id, today);
        if ((range.min ?? "") !== "" && value < Number(range.min)) return false;
        if ((range.max ?? "") !== "" && value > Number(range.max)) return false;
    }
    for (const [id, range] of Object.entries(spec.fecha || {})) {
        const value = filterFieldValue(obra, id, today);
        if (!value) return false;
        if ((range.min ?? "") !== "" && value < range.min) return false;
        if ((range.max ?? "") !== "" && value > range.max) return false;
    }
    return true;
}
